import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  useGetScopeTrackerWebsitesQuery,
  useUpdateDisplayScopeSelectionMutation,
  useRefreshScopeSelectionMutation,
  useGetScopeStatusQuery,
} from "../../app/api/console/consoleScopeTrackerApiSlice";

function ToggleButton({ enabled, loading, onClick, label }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
        enabled ? "bg-indigo-600" : "bg-gray-200"
      } ${loading ? "opacity-50 cursor-wait" : ""}`}
      role="switch"
      aria-checked={enabled}
      aria-label={label}>
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

const STATUS_CONFIG = {
  succeeded: { label: "Succeeded", bg: "bg-green-100", text: "text-green-800" },
  running: { label: "Running", bg: "bg-blue-100", text: "text-blue-800" },
  failed: { label: "Failed", bg: "bg-red-100", text: "text-red-800" },
  timed_out: { label: "Timed Out", bg: "bg-orange-100", text: "text-orange-800" },
  aborted: { label: "Aborted", bg: "bg-orange-100", text: "text-orange-800" },
  not_started: { label: "Not Started", bg: "bg-gray-100", text: "text-gray-600" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status?.toLowerCase()] || { label: status || "Unknown", bg: "bg-gray-100", text: "text-gray-500" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {status?.toLowerCase() === "running" && (
        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
      )}
      {cfg.label}
    </span>
  );
}

function RefreshButton({ websiteId, locales, scopeStatus }) {
  const [refreshScope, { isLoading: isRefreshing }] = useRefreshScopeSelectionMutation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isRunning = scopeStatus?.toLowerCase() === "running";
  const hasMultipleLocales = locales.length > 1;

  const handleRefresh = useCallback(
    async (localeCode = null) => {
      setShowDropdown(false);
      try {
        await refreshScope({ websiteId, localeCode }).unwrap();
      } catch (err) {
        console.error("Failed to refresh scope:", err);
      }
    },
    [refreshScope, websiteId]
  );

  if (!websiteId) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => (hasMultipleLocales ? setShowDropdown(!showDropdown) : handleRefresh())}
        disabled={isRefreshing || isRunning}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        title={isRunning ? "Scope selection is already running" : "Refresh scope selection"}>
        {isRefreshing || isRunning ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        )}
        Refresh{hasMultipleLocales ? " ▾" : ""}
      </button>

      {showDropdown && hasMultipleLocales && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px] z-20">
          <button
            onClick={() => handleRefresh()}
            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 font-medium text-indigo-700">
            Refresh All Locales
          </button>
          <div className="border-t border-gray-100 my-1" />
          {locales.map((l) => (
            <button
              key={l.locale_code}
              onClick={() => handleRefresh(l.locale_code)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50">
              {l.name} <span className="text-gray-400">({l.locale_code})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LocaleStatusRow({ websiteId, locales, scopeStatus }) {
  const shouldPoll = scopeStatus?.toLowerCase() === "running";
  const { data: statusData } = useGetScopeStatusQuery(websiteId, {
    skip: !websiteId || !shouldPoll,
    pollingInterval: shouldPoll ? 5000 : 0,
  });

  const localeStatuses = statusData?.locale_statuses || {};

  if (locales.length <= 1 && !Object.keys(localeStatuses).length) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-50 text-gray-600 border border-gray-200">
          {locales[0]?.locale_code || "en-US"}
        </span>
        {locales[0]?.locale_code && localeStatuses[locales[0].locale_code] && (
          <StatusBadge status={localeStatuses[locales[0].locale_code]?.status} />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {locales.map((l) => {
        const ls = localeStatuses[l.locale_code];
        return (
          <div
            key={l.locale_code}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-gray-50 border border-gray-200"
            title={l.url || l.name}>
            <span className="font-medium text-gray-700">{l.locale_code}</span>
            {ls?.status && (
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                ls.status === "SUCCEEDED" ? "bg-green-500" :
                ls.status === "RUNNING" ? "bg-blue-500 animate-pulse" :
                ls.status === "FAILED" || ls.status === "TIMED_OUT" || ls.status === "ABORTED" ? "bg-red-500" :
                "bg-gray-300"
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ScopeSelectionTracker() {
  const [search, setSearch] = useState("");

  const { data: websites = [], isLoading } = useGetScopeTrackerWebsitesQuery(undefined, {
    pollingInterval: 15000,
  });
  const [updateDisplayScope] = useUpdateDisplayScopeSelectionMutation();
  const [togglingIds, setTogglingIds] = useState(new Set());

  const filteredWebsites = useMemo(() => {
    if (!search.trim()) return websites;
    const q = search.toLowerCase();
    return websites.filter(
      (w) => w.website_name.toLowerCase().includes(q) || w.website_url.toLowerCase().includes(q)
    );
  }, [websites, search]);

  const handleToggle = async (website, useProd) => {
    const websiteId = useProd ? website.prod_website_id : website.staging_website_id;
    const currentValue = useProd ? website.prod_display_scope : website.staging_display_scope;
    const key = `${website.website_url}-${useProd ? "prod" : "staging"}`;

    setTogglingIds((prev) => new Set(prev).add(key));
    try {
      await updateDisplayScope({
        websiteId,
        websiteUrl: website.website_url,
        displayScopeSelection: !currentValue,
        useProd,
      }).unwrap();
    } catch (err) {
      console.error("Failed to update display_scope_selection:", err);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const stats = useMemo(() => {
    let staging = 0, prod = 0, running = 0, succeeded = 0, failed = 0;
    websites.forEach((w) => {
      if (w.staging_display_scope) staging++;
      if (w.prod_display_scope) prod++;
      const s = w.scope_status?.toLowerCase();
      if (s === "running") running++;
      else if (s === "succeeded") succeeded++;
      else if (s === "failed" || s === "timed_out" || s === "aborted") failed++;
    });
    return { staging, prod, running, succeeded, failed };
  }, [websites]);

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Scope Selection Tracker</h1>
        <p className="text-gray-600">Manage scope selection: view locales, status, toggle visibility, and trigger refreshes.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{websites.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Staging On</p>
          <p className="text-2xl font-bold text-indigo-700">{stats.staging}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Prod On</p>
          <p className="text-2xl font-bold text-green-700">{stats.prod}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Scope OK</p>
          <p className="text-2xl font-bold text-green-600">{stats.succeeded}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Running / Failed</p>
          <p className="text-2xl font-bold">
            <span className="text-blue-600">{stats.running}</span>
            {" / "}
            <span className="text-red-600">{stats.failed}</span>
          </p>
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or URL..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">Loading websites...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locales
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Scope Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Staging
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Prod
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWebsites.map((w) => {
                  const stagingKey = `${w.website_url}-staging`;
                  const prodKey = `${w.website_url}-prod`;
                  return (
                    <tr key={w.website_url} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{w.website_name}</div>
                        <a
                          href={w.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline truncate block max-w-[200px]">
                          {w.website_url}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <LocaleStatusRow
                          websiteId={w.staging_website_id}
                          locales={w.locales || []}
                          scopeStatus={w.scope_status}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={w.scope_status} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {w.staging_website_id ? (
                          <ToggleButton
                            enabled={w.staging_display_scope}
                            loading={togglingIds.has(stagingKey)}
                            onClick={() => handleToggle(w, false)}
                            label={`Toggle staging scope for ${w.website_name}`}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {w.prod_website_id ? (
                          <ToggleButton
                            enabled={w.prod_display_scope}
                            loading={togglingIds.has(prodKey)}
                            onClick={() => handleToggle(w, true)}
                            label={`Toggle prod scope for ${w.website_name}`}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <RefreshButton
                          websiteId={w.staging_website_id}
                          locales={w.locales || []}
                          scopeStatus={w.scope_status}
                        />
                      </td>
                    </tr>
                  );
                })}
                {filteredWebsites.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                      {search ? "No websites match your search." : "No websites found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScopeSelectionTracker;
